/**
 * Post-pointage modal shown right after a worker registered a time entry.
 * Lists all catalog articles (without prices) of the project's accepted quotes
 * so the worker can tick what they actually did today and enter quantities.
 *
 * Pure presentational + side-effects via /api/projects/article-completions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CheckCircle, Package } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostPointageArticles = ({ projectId, date, timeEntryId, onClose }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({}); // { lib_id: qty }

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/projects/${projectId}/quote-articles`, { withCredentials: true })
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const byCategory = useMemo(() => {
    const m = {};
    for (const a of articles) {
      const k = a.category || 'Non classé';
      (m[k] = m[k] || []).push(a);
    }
    return m;
  }, [articles]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = { ...s };
      if (id in next) delete next[id];
      else next[id] = 1;
      return next;
    });
  };
  const setQty = (id, v) => {
    const n = Math.max(0, parseFloat(String(v).replace(',', '.')) || 0);
    setSelected((s) => ({ ...s, [id]: n }));
  };

  const save = async () => {
    const items = Object.entries(selected)
      .filter(([, q]) => q > 0)
      .map(([id, qty]) => {
        const a = articles.find((x) => x.library_item_id === id);
        return {
          library_item_id: id,
          description: a?.description || '',
          category: a?.category || '',
          quantity: qty,
        };
      });
    if (items.length === 0) {
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/projects/article-completions`, {
        project_id: projectId, date, time_entry_id: timeEntryId, items,
      }, { withCredentials: true });
      toast.success(`${items.length} article(s) enregistré(s)`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      data-testid="post-pointage-modal">
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Articles réalisés aujourd'hui</h3>
            <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: 2 }}>
              Cochez ce que vous avez fait et indiquez la quantité.
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="post-pointage-close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
          {loading && <div style={{ textAlign: 'center', color: '#5E5F66' }}>Chargement…</div>}
          {!loading && articles.length === 0 && (
            <div style={{ textAlign: 'center', color: '#5E5F66', padding: 30 }} data-testid="post-pointage-empty">
              <Package size={36} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div>Aucun article au devis pour ce chantier.</div>
              <div style={{ fontSize: '0.78rem', marginTop: 6 }}>Vous pouvez fermer cette fenêtre.</div>
            </div>
          )}
          {Object.entries(byCategory).map(([cat, list]) => (
            <div key={cat} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.6, color: '#5E5F66', fontWeight: 700, marginBottom: 6 }}>{cat}</div>
              {list.map((a) => {
                const checked = a.library_item_id in selected;
                return (
                  <label key={a.library_item_id}
                    data-testid={`post-pointage-article-${a.library_item_id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', borderBottom: '1px solid #F4F5F7', cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(a.library_item_id)}
                      data-testid={`post-pointage-check-${a.library_item_id}`} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description}</div>
                      <div style={{ fontSize: '0.72rem', color: '#5E5F66', display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
                        <span data-testid={`post-pointage-total-${a.library_item_id}`}>
                          Total : <strong>{a.quantity_total}</strong> {a.unit || 'u'}
                        </span>
                        <span data-testid={`post-pointage-remaining-${a.library_item_id}`}
                          style={{ color: a.quantity_remaining > 0 ? '#0F766E' : '#B45309' }}>
                          Restant : <strong>{a.quantity_remaining}</strong> {a.unit || 'u'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" step="0.01" min="0"
                        value={checked ? selected[a.library_item_id] : ''}
                        placeholder="0"
                        onFocus={() => { if (!checked) toggle(a.library_item_id); }}
                        onChange={(e) => setQty(a.library_item_id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`post-pointage-qty-${a.library_item_id}`}
                        style={{ width: 80, padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 6, textAlign: 'right', fontSize: '0.85rem' }}
                      />
                      <span style={{ fontSize: '0.78rem', color: '#5E5F66', minWidth: 24 }}>{a.unit || 'u'}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{ padding: 14, borderTop: '1px solid #E2E2E6', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} data-testid="post-pointage-skip">
            Plus tard
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving} data-testid="post-pointage-save"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={16} /> {saving ? 'Enregistrement…' : 'Valider'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostPointageArticles;
